import { Flex, Icon, Box, Text } from '@cad-arc';
import React, { useEffect, useRef } from 'react';
import { Tooltip } from '../../../common/components/tooltip';
import { formatFullAddress, formatCrossStreets } from '../../helpers/location-display';
import { LocationStoreView } from '../../types/locations';
import { HighlightedText } from '../../../common/components/highlighted-text';
import {
    crossStreetWidth,
    dAreaWidth,
    esriAddressWidth,
    resolvedAddressWidth,
} from './location-table-v1';
import { LocationStore } from '../../location-store';
import { CommonSize, getBasicCommonSize, getOneSizeSmaller } from '../../../common/helpers/utils';

interface AddressRowProps {
    location: LocationStoreView;
    query: string;
    onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    index: number;
    store: LocationStore;
    size?: CommonSize;
}

export const LocationTableAddressRowV1: React.FC<AddressRowProps> = ({
    location,
    onClick,
    query,
    store,
    index,
    size,
}) => {
    const { isForcedResult } = location;
    const ref = useRef<HTMLDivElement | null>(null);
    const smallerSize = getOneSizeSmaller(size);

    useEffect(() => {
        if (
            ref.current &&
            store.highlightedLocationIndex === index &&
            store.shouldScrollLocationList
        ) {
            ref.current.scrollIntoView(false);
        }
    }, [store.highlightedLocationIndex, index, store.shouldScrollLocationList]);

    return (
        <Flex
            data-testid={`address-row-${location.id}`}
            justifyContent="flex-start"
            onMouseDown={(e) => onClick(e)}
            onMouseEnter={() => {
                store.setHighlightedLocationIndex(index, { source: 'mouse' });
            }}
            bg={index === store.highlightedLocationIndex ? 'interactive.hover' : undefined}
            ref={ref}
        >
            <Box
                pl={3}
                paddingY={1}
                w={
                    location.source === 'ESRI' || location.source === 'LAT_LONG'
                        ? esriAddressWidth
                        : resolvedAddressWidth
                }
            >
                <Tooltip overlay={formatFullAddress(location).toUpperCase()}>
                    <Flex justifyContent="space-between" alignItems="center">
                        <Flex
                            ml={isForcedResult ? 3 : 0}
                            color={isForcedResult ? 'text.secondary' : undefined}
                            direction="column"
                            cursor="default"
                        >
                            {location.placeName && (
                                <HighlightedText
                                    noOfLines={1}
                                    matchPattern={query}
                                    fontSize={smallerSize}
                                >
                                    {location.placeName}
                                </HighlightedText>
                            )}
                            {isForcedResult ? (
                                <Text fontSize={smallerSize}>{formatFullAddress(location)}</Text>
                            ) : (
                                <HighlightedText
                                    noOfLines={1}
                                    matchPattern={query}
                                    fontSize={smallerSize}
                                >
                                    {formatFullAddress(location)}
                                </HighlightedText>
                            )}
                        </Flex>
                        {location.areCoordinatesVerified && (
                            <Icon icon="Location" size={getBasicCommonSize(size)} />
                        )}
                    </Flex>
                </Tooltip>
            </Box>
            <Tooltip overlay={location.dispatchArea.toUpperCase()}>
                <Flex paddingX={3} paddingY={1} w={dAreaWidth} alignItems="center">
                    <Text noOfLines={1} fontSize={smallerSize}>
                        {location.dispatchArea}
                    </Text>
                </Flex>
            </Tooltip>
            <Tooltip overlay={formatCrossStreets(location)}>
                <Flex pl={3} paddingY={1} w={crossStreetWidth} alignItems="center">
                    <HighlightedText matchPattern={query} noOfLines={1} fontSize={smallerSize}>
                        {formatCrossStreets(location)}
                    </HighlightedText>
                </Flex>
            </Tooltip>
        </Flex>
    );
};
